import { requestPayment } from 'Cloud/Application/Payment';
import Promo from 'Cloud/Application/Promo';
import { promoController } from 'Cloud/Application/PromoController';
import config from 'Cloud/config';
import { logger } from 'lib/logger';
import store from 'lib/store';
import {
    GetExpiredAndroidSubscriptionAPICall,
    GetExpiredIosSubscriptionAPICall,
    TExpiredSubscription,
} from 'reactApp/api/billing/GetExpiredDeviceSubscriptionAPICall';
import { EmergencySwaAPICall } from 'reactApp/api/EmergencySwaAPICall';
import { BILLING_ENABLED, IS_IOS_MOBILE } from 'reactApp/appHelpers/configHelpers';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { renderEmergencySwaPopup, renderPromocodePopup } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog.helpers';
import { UNMODIFIED_CLOUD_SPACE } from 'reactApp/constants/magicIdentificators';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { isDialogVisible } from 'reactApp/modules/dialog/dialog.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getAbTrialPopup2Group } from 'reactApp/modules/features/features.selectors';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { productsController } from 'reactApp/modules/products/products.controller';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { updateUser } from 'reactApp/modules/user/user.thunkActions';
import { renderBannerOffer } from 'reactApp/ui/BannerOffer/BannerOffer.helpers';
import { canCheckExpiredSubs, canShowExpiredScreen } from 'reactApp/ui/Mobile/SplashScreen/helpers/ExpiredSubscriptionScreen.helpers';
import { renderMobileSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/helpers/SplashScreen.helpers';
import { useExpiredSubscriptionScreen } from 'reactApp/ui/Mobile/SplashScreen/hooks/useExpiredSubscriptionScreen';
import { useOverquotaSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/hooks/useOverquotaSplashScreen';
import { renderOverquotaBanner } from 'reactApp/ui/OverquotaPopup/OverquotaPopup.helpers';
import { SPACE_COUNTDOWN_ID } from 'reactApp/ui/Space/SpaceCountdown/SpaceCountdown.data';
import { findHigherTariff } from 'reactApp/utils/findTariff';
import { noop } from 'reactApp/utils/helpers';
import { isYearPeriod } from 'reactApp/utils/Period';
import { channel } from 'redux-saga';
import { all, call, cancel, put, select, take } from 'redux-saga/effects';

import { AB_TEST_TRIAL } from '../home.constants';

export function* showEmergencySwaPopup() {
    let link = '';

    try {
        const { data } = yield call(new EmergencySwaAPICall().makeRequest);
        logger.verbose('EmergencySwa:', data);
        link = data.link;
    } catch (error) {
        logger.error(error);
        yield cancel();
    }

    const popupChannel = channel();

    yield renderEmergencySwaPopup({
        link,
        onClose: () => popupChannel.put(true),
    });

    yield take(popupChannel);
    yield popupChannel.close();

    yield closePopupHelper(popupNames.EMERGENCY_SWA);
}

export function* showPromocodePopup() {
    const promocodeChannel = channel();

    yield renderPromocodePopup({
        onSuccess: () => promocodeChannel.put(true),
        onClose: () => promocodeChannel.put(true),
    });

    yield take(promocodeChannel);
    yield promocodeChannel.close();

    yield closePopupHelper(popupNames.WHATS_NEW_DIALOG);

    // @ts-ignore
    yield put(updateUser());
}

export function* showOverquotaPopup() {
    const isOverQuota = yield select(UserSelectors.isOverQuotaUser);
    const { over } = yield select(UserSelectors.getCloudSpace);
    const isPhone = yield select(EnvironmentSelectors.isPhone);
    const isAnonymous = yield select(UserSelectors.isAnonymous);
    const storage = yield select(getCurrentStorage);

    if (!isOverQuota || isAnonymous) {
        return;
    }

    const tariffs = yield productsController.getAvailableProducts();
    const tariffsForOverquota = tariffs.filter((item): boolean => !item?.products.every((product) => product.hasTrial));

    if (!tariffsForOverquota.length) {
        return;
    }

    const tariff = findHigherTariff({ tariffs: tariffsForOverquota, quota: over.original, period: '1y' }) || tariffsForOverquota.pop();
    const product = tariff.products.find((product) => isYearPeriod(product.period)) || tariff.products[0];

    if (isPhone) {
        if (storage === EStorageType.subscriptions) {
            return;
        }
        yield call(renderMobileSplashScreen, { contentHook: useOverquotaSplashScreen, productId: product.id });
    } else {
        yield call(renderOverquotaBanner, { tariff });
    }
}

export function* showQuotaPopup() {
    const { isFull, total, free }: ReturnType<typeof UserSelectors.getCloudSpace> = yield select(UserSelectors.getCloudSpace);
    const email: ReturnType<typeof UserSelectors.getEmail> = yield select(UserSelectors.getEmail);

    const bytesFree = free?.original;
    const bytesTotal = total?.original;

    if (!bytesFree || !bytesTotal || !email || isFull) {
        return;
    }

    if (bytesFree >= SPACE_LIST.gb_16) {
        return;
    }

    let quota;

    if (bytesFree < SPACE_LIST.gb_16 && bytesTotal >= SPACE_LIST.gb_64) {
        quota = 16;
    }

    if (bytesFree < SPACE_LIST.gb_8 && bytesTotal >= SPACE_LIST.gb_32) {
        quota = 8;
    }

    if (bytesFree < SPACE_LIST.gb_4) {
        quota = 4;
    }

    if (bytesFree < SPACE_LIST.gb_2) {
        quota = 2;
    }

    if (!quota) {
        return;
    }

    const bannerId = `quota-banner_${quota}`;

    const quotaPromo = Object.assign(new Promo(bannerId), {
        isEnabled: () => {
            const endDate = store.get(`${email}|${bannerId}`);
            return !endDate || (endDate && Date.now() > endDate);
        },
    });

    promoController.register(10, quotaPromo);

    if (promoController.canShow(bannerId)) {
        openPopupHelper({
            popupName: popupNames.QUOTA_POPUP,
            data: {
                onSuccess: noop,
                onClose: noop,
                quota,
            },
        });
    }
}

let BANNER_OFFER_CONFIG = config.get('BANNER_OFFER');

export function* showBannerOfferPopup() {
    const promoCountdown = new Promo(`promo-countdown_${SPACE_COUNTDOWN_ID}`);
    const storage = yield select(getCurrentStorage);
    const { isHome } = getStorage(storage);
    const isPaidUser = yield select(UserSelectors.isPaidUser);
    const email = yield select(UserSelectors.getEmail);
    const isOldBizUser = yield select(UserSelectors.isOldBizUser);
    const isB2BUser = yield select(UserSelectors.isB2BUser);
    const isBizUser = yield select(UserSelectors.isBizUser);
    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const { usedBytes } = yield select(UserSelectors.getCloudSpace);
    const isOverQuotaUser = yield select(UserSelectors.isOverQuotaUser);

    if (
        isPaidUser ||
        !email ||
        !isHome ||
        isOldBizUser ||
        isB2BUser ||
        isBizUser ||
        isCorpUser ||
        isOverQuotaUser ||
        usedBytes === UNMODIFIED_CLOUD_SPACE || // Не загружал еще ни одного файла
        usedBytes === 0
    ) {
        return;
    }

    const abTrialGroup = yield select(getAbTrialPopup2Group);
    if (!BANNER_OFFER_CONFIG || abTrialGroup) {
        BANNER_OFFER_CONFIG = AB_TEST_TRIAL[abTrialGroup === 'B' ? 1 : 0];
    }

    const bannerPromo = new Promo(BANNER_OFFER_CONFIG.id);

    promoController.register(3000, bannerPromo);

    if (!promoController.canShow(BANNER_OFFER_CONFIG.id)) {
        return;
    }

    try {
        const [tariff, isUserWithoutPayment] = yield all([
            productsController.getTariffById(BANNER_OFFER_CONFIG.tariffId),
            productsController.isUserWithoutPayment(),
        ]);

        const isAnyDialogShown = yield select(isDialogVisible);

        if (!tariff || !isUserWithoutPayment || isAnyDialogShown) {
            promoController.unregister(3000);
            return;
        }

        // Время для promo-countdown устанавливается тут:
        // reactApp/ui/Space/spacePromo.js -> getPromoCountdownEndDate
        const isPromoCountdownShown = promoCountdown.isShownUntillTime({ time: new Date() });
        if (isPromoCountdownShown) {
            return;
        }

        const hidePopup = () => promoController.markAsShown(BANNER_OFFER_CONFIG.id, { daysCount: 7 });

        const product = tariff?.products?.[0];

        BANNER_OFFER_CONFIG.productId = product?.id;

        yield call(renderBannerOffer, {
            ...BANNER_OFFER_CONFIG,
            onClose: hidePopup,
            onClick: hidePopup,
            onShow: hidePopup,
            product,
        });
    } catch (error) {
        logger.error(error);
    }
}

export function* showRequestPayment() {
    const isB2BUser = yield select(UserSelectors.isB2BUser);
    const isFrozen = yield select(UserSelectors.isFrozen);
    const queryParams = getQueryParams() || {};
    const { action, tariff, tariffId, productId } = queryParams;
    let { quota } = queryParams;

    if (isB2BUser || isFrozen || !BILLING_ENABLED || action !== 'request-payment') {
        return;
    }

    if (tariff) {
        const exponent = decodeURIComponent(tariff).toUpperCase().includes('ГБ') ? 3 : 4;
        quota = parseInt(tariff) * 1024 ** exponent;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    yield call(requestPayment, { tariff: tariffId || quota, productId });
}

export function* showExpiredDeviceSubscription() {
    const isOverQuota = yield select(UserSelectors.isOverQuotaUser);
    const isPaidUser = yield select(UserSelectors.isPaidUser);
    const isPhone = yield select(EnvironmentSelectors.isPhone);
    const isAnonymous = yield select(UserSelectors.isAnonymous);
    const isBizUser = yield select(UserSelectors.isBizUser);
    const email = yield select(UserSelectors.getEmail);
    const storage = yield select(getCurrentStorage);

    if (isAnonymous || isOverQuota || isPaidUser || isBizUser || !isPhone || storage === EStorageType.subscriptions) {
        return;
    }

    const canShowScreen = canShowExpiredScreen(email);
    const canCheckSubs = canCheckExpiredSubs(email);

    if (!canShowScreen && !canCheckSubs) {
        return;
    }

    try {
        const apiCall = IS_IOS_MOBILE ? new GetExpiredIosSubscriptionAPICall() : new GetExpiredAndroidSubscriptionAPICall();
        const { data }: { data: TExpiredSubscription } = yield call(apiCall.makeRequest);

        const period = data?.period;
        const quota = data?.services?.quota;

        if (!quota) {
            return;
        }

        yield productsController.loadProducts();

        const product = yield select(
            ProductsSelectors.getExpiredDeviceTariff,
            quota < SPACE_LIST.gb_32 ? SPACE_LIST.gb_32 : quota,
            period || '1y'
        );

        if (!product) {
            return;
        }

        yield call(renderMobileSplashScreen, { contentHook: useExpiredSubscriptionScreen, productId: product.id });
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}
